import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { User, PaymentMethodProfile } from "models/propTypes/userProfilePropTypes";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { ReceiptListItemExtended, ReceiptListViewProps } from "models/propTypes/receiptPropTypes";
import { ReceiptFormTypes } from "models/formTypes";
import { Amount } from "models/utilTypes/currencyType";
import { ColumnVisibility, TableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { TableAsync, CurrencyValueFormatter } from "components/common";
import ReceiptStatusTags from "./statusTags";
import ReceiptPaymentStatus from "./paymentStatus";
import { useFilterContext } from "components/common";
import { ReceiptService } from "services";
import { useReceiptService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, CurrencyHelper, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const ReceiptListView: React.ForwardRefRenderFunction<TableHandles, ReceiptListViewProps> = (props, ref) => {
  const receiptService = useReceiptService();
  const { filter } = useFilterContext<ReceiptFormTypes.Filter>();
  const { reloadKey, hiddenColumns } = props;

  const currentUser = useSelector((store: AppState) => store.auth.loggedInUser.personId);

  const filterFunc = React.useCallback(
    (baseFilter: WintUtilsWebApi.Filter) =>
      receiptService.reportFilter(
        baseFilter,
        {
          ...(props.filter ? props.filter : (filter ?? {})),
        },
        currentUser
      ),
    [filter]
  );

  const receiptCategories = useSelector(
    (x: AppState) => x.auth.selectedCompany.receiptCategories ?? [],
    (left, right) => left?.map((l) => l.id).join("") === right?.map((r) => r.id).join("")
  );

  const getItemUrl = (id: number | string) => UrlHelper.formatPath(`/Receipt/${id}`);

  return (
    <TableAsync<ReceiptListItemExtended>
      tableRef={ref}
      properties={{
        createdByPersonName: {
          description: TranslationKeys.Common.Owner,
          formatter: (receipt: WintSuperkollAPIModel.Receipt) =>
            new User(receipt.createdByPersonId, receipt.createdByPersonName),
          helpText: TranslationKeys.Receipt.CreatedByPersonHelpText,
          noWrap: true,
        },
        dateTime: {
          description: TranslationKeys.Receipt.ReceiptPurchaseDate,
          formatter: (receipt: WintSuperkollAPIModel.Receipt) => receipt.dateTime && new Date(receipt.dateTime),
          helpText: TranslationKeys.Receipt.PurchaseDateHelpText,
          maxWidth: 80,
          noWrap: true,
        },
        receiptCategoryId: {
          description: TranslationKeys.Common.CategoryRefers,
          formatter: (receipt: WintSuperkollAPIModel.Receipt) => {
            const receiptCategory = receipt
              ? ReceiptService.findReceiptCategoryByCategoryId(receipt.receiptCategoryId, receiptCategories)
              : undefined;
            return receiptCategory && receiptCategory.name?.toString();
          },
          helpText: TranslationKeys.Receipt.ReceiptCategoryHelpText,
          maxWidth: 100,
          noWrap: true,
        },
        paymentMethodName: {
          description: TranslationKeys.Common.PaymentMethod,
          helpText: TranslationKeys.Receipt.ReceiptPaymentMethodHelpText,
          formatter: (receipt: WintSuperkollAPIModel.Receipt) => {
            // If the receipt is marked as "Other", just return the name
            if (receipt.isOtherPaymentMethod) {
              return receipt.paymentMethodName;
            }
            return receipt.createdByPersonId === currentUser
              ? new PaymentMethodProfile(receipt.paymentMethodId ?? 0, receipt.paymentMethodName ?? "")
              : receipt.paymentMethodName;
          },
          noWrap: true,
        },
        serialNumber: {
          description: TranslationKeys.Common.Number,
          helpText: TranslationKeys.Receipt.SerialNumberHelpText,
          maxWidth: 30,
          alignment: "right",
          noWrap: true,
        },
        amount: {
          description: TranslationKeys.Common.TotalAmount,
          formatter: (receipt: WintSuperkollAPIModel.Receipt) => new Amount(receipt.currency, receipt.amount),
          className: cssExports.textBlack,
          alignment: "right",
          width: 130,
          summaryValueFormatter: (values: WintSuperkollAPIModel.Receipt) => (
            <CurrencyValueFormatter sumValue={values.totalAmountSummarySEK} cellValue={values.totalAmountSummary} />
          ),
          summaryToPrint: (values: WintSuperkollAPIModel.Receipt) =>
            CurrencyHelper.getSummaryToPrintWithSum(values.totalAmountSummarySEK, values.totalAmountSummary),
          helpText: TranslationKeys.Receipt.AmountHelpText,
          noWrap: true,
        },
        totalAmountExVat: {
          description: TranslationKeys.Common.AmountExVat2,
          visibility: ColumnVisibility.ExportOnly,
        },
        leftToPay: {
          description: TranslationKeys.Common.LeftToPay,
          visibility: ColumnVisibility.ExportOnly,
        },
        state: {
          description: TranslationKeys.Common.Status,
          formatter: (receipt: WintSuperkollAPIModel.Receipt) =>
            receipt.state !== undefined ? <ReceiptStatusTags status={receipt.state} /> : undefined,
          helpText: TranslationKeys.Receipt.AccountingStatusHelpText,
          maxWidth: 100,
          valueToPrint: (receipt: WintSuperkollAPIModel.Receipt) =>
            receipt.state !== undefined
              ? receiptService.getStateText(receiptService.apiToViewStatus(receipt.state))
              : "",
        },
        paymentState: {
          description: TranslationKeys.Common.Paid2,
          formatter: (values: WintSuperkollAPIModel.Receipt) => <ReceiptPaymentStatus status={values.paymentState} />,
          alignment: "center",
          helpText: TranslationKeys.Receipt.PaymentStatus,
          maxWidth: 40,
          valueToPrint: (receipt: WintSuperkollAPIModel.Receipt) =>
            receiptService.getPaymentStatusText(receipt.paymentState),
        },
      }}
      filter={filterFunc}
      includeSummary
      maxRowCount={10}
      pagination="advanced"
      reloadKey={reloadKey}
      orderByProperty={"dateTime"}
      orderByDescending
      itemUrl={getItemUrl}
      hiddenColumns={hiddenColumns}
      exportFileName={
        props.exportFileName ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.Receipts)
      }
    />
  );
};
export default React.forwardRef(ReceiptListView);
